import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Pencil } from 'lucide-react';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { formatDateTime } from '../../../lib/dateUtils';
import { API_URL, auth, errMsg, rand } from '../financeApi';
import { StatusPill, METHOD_LABEL } from './payShared';

const PaymentLine = ({ p, onCorrect }) => (
  <li className="text-sm border rounded-lg p-2" data-testid={`history-payment-${p.payment_id}`}>
    <div className="flex items-center justify-between gap-2">
      <span><strong>{rand(p.amount)}</strong> {METHOD_LABEL[p.method]} · paid {p.date_paid} · by {p.recorded_by_name}</span>
      {onCorrect && <Button size="sm" variant="ghost" onClick={() => onCorrect(p)} data-testid={`history-correct-${p.payment_id}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Correct</Button>}
    </div>
    {p.corrections.map((c, i) => (
      <p key={i} className="text-xs text-muted-foreground mt-1">
        Corrected {formatDateTime(c.at)} by {c.by_name}: {c.changes.map((x) => `${x.field.replace('_', ' ')} ${x.field === 'amount' ? rand(x.old) : x.old} → ${x.field === 'amount' ? rand(x.new) : x.new}`).join(', ')} · {c.reason}
      </p>
    ))}
  </li>
);

export const KidHistoryDialog = ({ kid, onClose, token, onCorrect, refreshKey }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!kid) return;
    setData(null);
    axios.get(`${API_URL}/finance/parent-payments/kids/${kid.kid_id}/history`, auth(token))
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errMsg(e, 'Could not load history')));
  }, [kid, token, refreshKey]);

  return (
    <Dialog open={!!kid} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="kid-history-dialog">
        <DialogHeader>
          <DialogTitle>Payment history · {kid?.full_name}</DialogTitle>
          <DialogDescription>Each month stands alone. Extra money paid is kept as credit for the next month.</DialogDescription>
        </DialogHeader>
        {!data && <p className="text-sm text-muted-foreground">Loading…</p>}
        {data && data.months.length === 0 && <p className="text-sm text-muted-foreground" data-testid="history-empty">No fee or payments yet.</p>}
        {data && data.fee_schedule.length > 0 && (
          <p className="text-xs text-muted-foreground" data-testid="history-fee-schedule">
            Fee changes: {data.fee_schedule.map((s) => `${rand(s.amount)} from ${s.from_month}`).join(' · ')}
          </p>
        )}
        <div className="space-y-3">
          {data?.months.map((m) => (
            <section key={m.month} className="rounded-xl border p-3 space-y-2" data-testid={`history-month-${m.month}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold">{m.label}</p>
                <StatusPill status={m.status} testid={`history-status-${m.month}`} />
              </div>
              <p className="text-xs text-muted-foreground">
                Expected {rand(m.expected)}{m.special_fee ? ` (special fee${m.special_note ? `: ${m.special_note}` : ''})` : ''}
                {m.credit_in > 0 ? ` · credit from last month ${rand(m.credit_in)}` : ''} · received {rand(m.received)} · owed {rand(m.owed)}
                {m.credit_out > 0 ? ` · credit to next month ${rand(m.credit_out)}` : ''}
              </p>
              {m.payments.length > 0 && <ul className="space-y-1.5">{m.payments.map((p) => <PaymentLine key={p.payment_id} p={p} onCorrect={onCorrect ? (x) => onCorrect(x, m.month) : null} />)}</ul>}
            </section>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
